import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../utils/store";

export interface OrgMembership {
  organizationId: string;
  roleSlug?: string;
  canViewAuditLogs?: boolean;
  isPrimary?: boolean;
  organization?: {
    _id: string;
    name: string;
    slug: string;
    brandName?: string;
    status?: string;
    logoUrl?: string;
  } | null;
}

export interface AuthState {
  authenticated: boolean;
  accessToken: string;
  loading: boolean;
  userName: string;
  email: string;
  role?: string;
  hierarchyLevel?: string;
  userId: string;
  lastUpdate: string;
  privileges: any;
  isFirstLogin: any;
  uuid: any;
  isSuperAdmin?: boolean;
  isAdmin?: boolean;
  activeOrganizationId?: string | null;
  requiresOrgSelection?: boolean;
  memberships?: OrgMembership[];
}

const initialState: AuthState = {
  authenticated: false,
  accessToken: "",
  loading: false,
  userName: "",
  email: "",
  role: "",
  hierarchyLevel: "",
  userId: "",
  lastUpdate: "",
  privileges: {},
  isFirstLogin: "",
  uuid: "",
  isSuperAdmin: false,
  isAdmin: false,
  activeOrganizationId: null,
  requiresOrgSelection: false,
  memberships: [],
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginAction: (
      state,
      action: PayloadAction<{
        authenticated: boolean;
        accessToken: string;
        userName: string;
        email: string;
        role: string;
        hierarchyLevel: string;
        userId: string;
        lastUpdate: string;
        privileges: any;
        isFirstLogin: any;
        uuid: any;
        isSuperAdmin?: boolean;
        isAdmin?: boolean;
        activeOrganizationId?: string | null;
        requiresOrgSelection?: boolean;
        memberships?: OrgMembership[];
      }>
    ) => {
      state.authenticated = action.payload.authenticated;
      state.accessToken = action.payload.accessToken;
      state.userName = action.payload.userName;
      state.email = action.payload.email;
      state.role = action.payload.role;
      state.hierarchyLevel = action.payload.hierarchyLevel;
      state.userId = action.payload.userId;
      state.lastUpdate = action.payload.lastUpdate;
      state.privileges = action.payload.privileges;
      state.isFirstLogin = action.payload.isFirstLogin;
      state.uuid = action.payload.uuid;
      state.isSuperAdmin = action.payload.isSuperAdmin || false;
      state.isAdmin = action.payload.isAdmin || false;
      state.activeOrganizationId =
        action.payload.activeOrganizationId ?? null;
      state.requiresOrgSelection =
        action.payload.requiresOrgSelection || false;
      state.memberships = action.payload.memberships || [];
    },
    setActiveOrganization: (
      state,
      action: PayloadAction<{
        activeOrganizationId: string;
        accessToken?: string;
      }>
    ) => {
      state.activeOrganizationId = action.payload.activeOrganizationId;
      if (action.payload.accessToken) {
        state.accessToken = action.payload.accessToken;
      }
      state.requiresOrgSelection = false;
    },
    setMemberships: (
      state,
      action: PayloadAction<{
        memberships: OrgMembership[];
        isSuperAdmin?: boolean;
        activeOrganizationId?: string | null;
      }>
    ) => {
      state.memberships = action.payload.memberships || [];
      if (typeof action.payload.isSuperAdmin === "boolean") {
        state.isSuperAdmin = action.payload.isSuperAdmin;
      }
      if (action.payload.activeOrganizationId !== undefined) {
        state.activeOrganizationId = action.payload.activeOrganizationId;
      }
    },
    addLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setUuid: (state, action: PayloadAction<string>) => {
      state.uuid = action.payload;
    },

    logOutAction: (state, action: {}) => {
      state.authenticated = false;
      state.loading = false;
      state.accessToken = "";
      state.userName = "";
      state.email = "";
      state.role = "";
      state.hierarchyLevel = "";
      state.userId = "";
      state.lastUpdate = "";
      state.privileges = {};
      state.isFirstLogin = "";
      state.uuid = "";
      state.isSuperAdmin = false;
      state.isAdmin = false;
      state.activeOrganizationId = null;
      state.requiresOrgSelection = false;
      state.memberships = [];
      try {
        sessionStorage.removeItem("org_scope_reloaded");
      } catch (_) {
        /* ignore */
      }
    },
  },
});

export const {
  loginAction,
  logOutAction,
  addLoading,
  setUuid,
  setActiveOrganization,
  setMemberships,
} = authSlice.actions;

export const selectAuthenticated = (state: RootState) =>
  !!state.auth.authenticated && !!state.auth.accessToken;
export const selectAccessToken = (state: RootState) => state.auth.accessToken;
export const selectLoading = (state: RootState) => state.auth.loading;
export const selectEmail = (state: RootState) => state.auth.email;
export const selectName = (state: RootState) => state.auth.userName;
export const selectRole = (state: RootState) => state.auth.role;
export const selectRole1 = (state: RootState) => state.auth.hierarchyLevel;
export const selectUserId = (state: RootState) => state.auth.userId;
export const selectLastUpdate = (state: RootState) => state.auth.lastUpdate;
export const selectPrivileges = (state: RootState) => state.auth.privileges;
export const selectFirstLogin = (state: RootState) => state.auth.isFirstLogin;
export const selectUiid = (state: RootState) => state.auth.uuid;
export const selectActiveOrganizationId = (state: RootState) =>
  state.auth.activeOrganizationId;
export const selectMemberships = (state: RootState) =>
  state.auth.memberships || [];
export const selectIsSuperAdmin = (state: RootState) =>
  !!state.auth.isSuperAdmin;
export const selectRequiresOrgSelection = (state: RootState) =>
  !!state.auth.requiresOrgSelection;

export default authSlice.reducer;
